<template>
  <div class="lotteries-accordion" v-show="regionLotteriesParameter">
    <div 
      v-for="(lotteries, lotteryName) in groupedLotteries" 
      :key="lotteryName"
      class="accordion-item"
    >
      <div 
        class="accordion-header"
        @click="toggleAccordion(lotteryName)"
        :class="{ 'active': expandedLotteries[lotteryName] }"
      >
        <span class="accordion-icon">
          {{ expandedLotteries[lotteryName] ? '▼' : '►' }}
        </span>
        <span class="lottery-name">{{ lotteryName }}</span>
        <span class="lottery-count">({{ lotteries.length }} horário{{ lotteries.length > 1 ? 's' : '' }})</span>
      </div>
      
      <div 
        class="accordion-content"
        v-show="expandedLotteries[lotteryName]"
      >
        <table class="styled-table">
          <thead>
            <tr>
              <th style="text-align: left;">Horário</th>
              <th>Seg</th>
              <th>Ter</th>
              <th>Qua</th>
              <th>Qui</th>
              <th>Sex</th>
              <th>Sáb</th>
              <th>Dom</th>
              <th>Max. Apostas</th>
              <th>Max. Cancelar</th>
              <th>Ativo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in lotteries" :key="d.id">
              <td class="schedule-cell">{{ d.lotteryName }} - {{ d.lotteryDetailDescription }}</td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.monday" 
                  :disabled="isReadOnly"
                />
              </td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.tuesday" 
                  :disabled="isReadOnly"
                />
              </td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.wednesday" 
                  :disabled="isReadOnly"
                />
              </td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.thursday" 
                  :disabled="isReadOnly"
                />
              </td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.friday" 
                  :disabled="isReadOnly"
                />
              </td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.saturday" 
                  :disabled="isReadOnly"
                />
              </td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.sunday" 
                  :disabled="isReadOnly"
                />
              </td>
              <td class="time-cell">
                <input 
                  type="time" 
                  v-model="d.maxHourToAcceptBet" 
                  :disabled="isReadOnly"
                  :readonly="isReadOnly"
                  class="time-input"
                />
              </td>
              <td class="time-cell">
                <div class="time-selector-container"
                     :title="'Tempo que o vendedor tem para cancelar após criar a aposta'"
                     :class="{ 'tooltip-container': true }">
                  <div class="time-selector">
                    <select
                      :value="getCancelHours(d)"
                      :disabled="isReadOnly"
                      @change="updateCancelHours(d, $event.target.value)"
                      class="time-select hours-select"
                    >
                      <option v-for="hour in 24" :key="hour-1" :value="String(hour-1).padStart(2, '0')">
                        {{ String(hour-1).padStart(2, '0') }}h
                      </option>
                    </select>
                    <span class="time-separator">:</span>
                    <select
                      :value="getCancelMinutes(d)"
                      :disabled="isReadOnly"
                      @change="updateCancelMinutes(d, $event.target.value)"
                      class="time-select minutes-select"
                    >
                      <option v-for="minute in minuteOptions" :key="minute" :value="minute">
                        {{ minute }}m
                      </option>
                    </select>
                  </div>
                </div>
              </td>
              <td class="checkbox-cell">
                <input 
                  type="checkbox" 
                  v-model="d.isActive" 
                  :disabled="isReadOnly"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!isReadOnly" class="save-button-container">
      <button
        type="button"
        class="btn btn-save"
        @click="saveChanges"
        :disabled="saving"
      >
        <i class="fa-solid fa-save"></i>
        {{ saving ? 'Salvando...' : 'Salvar Configuração' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    regionLotteriesParameter: {
      type: Array,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ["save-changes"],
  data: () => ({
    minuteOptions: ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'],
    expandedLotteries: {},
    saving: false
  }),
  computed: {
    groupedLotteries() {
      if (!this.regionLotteriesParameter) return {};
      
      const grouped = {};
      this.regionLotteriesParameter.forEach(lottery => {
        const lotteryName = lottery.lotteryName;
        if (!grouped[lotteryName]) {
          grouped[lotteryName] = [];
        }
        grouped[lotteryName].push(lottery);
      });
      
      // Ordenar os horários dentro de cada grupo
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => {
          const timeA = a.lotteryDetailDescription || '';
          const timeB = b.lotteryDetailDescription || '';
          return timeA.localeCompare(timeB);
        });
      });
      
      return grouped;
    }
  },
  methods: {
    toggleAccordion(lotteryName) {
      this.expandedLotteries[lotteryName] = !this.expandedLotteries[lotteryName];
      // Forçar atualização da reatividade
      this.$forceUpdate();
    },
    getCancelHours(lottery) {
      if (!lottery.maxHourToCancelBet) {
        return '00';
      }
      // TimeOnly pode vir como "HH:mm:ss.fffffff" ou "HH:mm:ss" ou "HH:mm"
      if (typeof lottery.maxHourToCancelBet === 'string') {
        // Remove milissegundos se existirem
        const timeWithoutMs = lottery.maxHourToCancelBet.split('.')[0];
        const parts = timeWithoutMs.split(':');
        return parts[0] || '00';
      }
      return '00';
    },

    getCancelMinutes(lottery) {
      if (!lottery.maxHourToCancelBet) {
        return '00';
      }
      // TimeOnly pode vir como "HH:mm:ss.fffffff" ou "HH:mm:ss" ou "HH:mm"
      if (typeof lottery.maxHourToCancelBet === 'string') {
        // Remove milissegundos se existirem
        const timeWithoutMs = lottery.maxHourToCancelBet.split('.')[0];
        const parts = timeWithoutMs.split(':');
        return parts[1] || '00';
      }
      return '00';
    },

    updateCancelHours(lottery, newHours) {
      if (!this.isReadOnly) {
        const hours = String(newHours || '00').padStart(2, '0');
        const minutes = this.getCancelMinutes(lottery);
        // Manter formato HH:mm para compatibilidade no frontend
        const newTime = `${hours}:${minutes}`;
        
        // Atualizar diretamente o campo
        lottery.maxHourToCancelBet = newTime;
      }
    },

    updateCancelMinutes(lottery, newMinutes) {
      if (!this.isReadOnly) {
        const hours = this.getCancelHours(lottery);
        const minutes = String(newMinutes || '00').padStart(2, '0');
        const newTime = `${hours}:${minutes}`;
        lottery.maxHourToCancelBet = newTime;
      }
    },

    saveChanges() {
      if (this.isReadOnly || this.saving) {
        return;
      }

      this.saving = true;
      this.$emit("save-changes");
    },

    resetSaving() {
      this.saving = false;
    }
  },
  mounted() {
    // Garantir que todos os objetos tenham o campo maxHourToCancelBet inicializado
    if (this.regionLotteriesParameter) {
      this.regionLotteriesParameter.forEach(lottery => {
        if (!lottery.maxHourToCancelBet) {
          lottery.maxHourToCancelBet = '00:00';
        }
      });
    }
  },
  watch: {
    regionLotteriesParameter: {
      handler(newValue) {
        if (newValue) {
          newValue.forEach(lottery => {
            if (!lottery.maxHourToCancelBet) {
              lottery.maxHourToCancelBet = '00:00';
            }
          });
        }
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style scoped>
/* Botão de salvar */
.save-button-container {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 2px solid #3b82f6;
  display: flex;
  justify-content: center;
}

[data-theme="dark"] .save-button-container {
  border-top-color: #60a5fa;
}

.btn-save {
  padding: 10px 30px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
  border: none;
  background-color: #28a745;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-save:hover:not(:disabled) {
  background-color: #218838;
  transform: translateY(-1px);
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Container do Acordeão */
.lotteries-accordion {
  width: 100%;
  max-width: 100%;
  overflow: visible;
  min-height: auto;
}

/* Item do Acordeão */
.accordion-item {
  margin-bottom: 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.accordion-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

[data-theme="dark"] .accordion-item {
  background-color: rgba(37, 37, 41, 0.9);
  border-color: #52525b;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3);
}

[data-theme="dark"] .accordion-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.4);
}

/* Cabeçalho do Acordeão */
.accordion-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  background-color: #f1f5f9;
  transition: all 0.3s ease;
  user-select: none;
  border-radius: 8px;
}

.accordion-header:hover {
  background-color: #e2e8f0;
}

.accordion-header.active {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

[data-theme="dark"] .accordion-header {
  background-color: #1a1a1d;
}

[data-theme="dark"] .accordion-header:hover {
  background-color: #252529;
}

[data-theme="dark"] .accordion-header.active {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
}

.accordion-icon {
  font-size: 14px;
  margin-right: 12px;
  transition: transform 0.3s ease;
  color: #64748b;
}

[data-theme="dark"] .accordion-icon {
  color: #a1a1aa;
}

.accordion-header.active .accordion-icon {
  color: white;
}

.lottery-name {
  font-weight: 600;
  font-size: 16px;
  flex: 1;
  color: #1e293b;
}

[data-theme="dark"] .lottery-name {
  color: #f4f4f5;
}

.accordion-header.active .lottery-name {
  color: white;
}

.lottery-count {
  font-size: 14px;
  opacity: 0.8;
  margin-left: 8px;
  color: #475569;
}

[data-theme="dark"] .lottery-count {
  color: #e4e4e7;
}

.accordion-header.active .lottery-count {
  color: white;
}

/* Conteúdo do Acordeão */
.accordion-content {
  padding: 0;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  max-height: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

/* Tabela */
.styled-table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  font-size: 13px;
}

.styled-table thead {
  background-color: #1a1a1d;
}

[data-theme="dark"] .styled-table thead {
  background-color: #1a1a1d;
}

.styled-table th {
  padding: 10px 8px;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid #52525b;
  font-size: 12px;
  white-space: nowrap;
  color: #ffffff;
  vertical-align: middle;
}

[data-theme="dark"] .styled-table th {
  border-bottom-color: #52525b;
  color: #f4f4f5;
}

.styled-table td {
  padding: 10px 8px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  color: #1e293b;
}

[data-theme="dark"] .styled-table td {
  border-bottom-color: #52525b;
  color: #f4f4f5;
  background: #0e0e0f;
}

.styled-table tbody tr:hover {
  background-color: #f8fafc;
}

[data-theme="dark"] .styled-table tbody tr:hover {
  background-color: #252529;
}

/* Células específicas */
.schedule-cell {
  font-weight: 600;
  color: #1e293b;
  text-align: left !important;
  padding-left: 16px !important;
  min-width: 150px;
  vertical-align: middle;
}

[data-theme="dark"] .schedule-cell {
  color: #f4f4f5;
}

.checkbox-cell {
  width: 50px;
  padding: 10px 4px !important;
}

.time-cell {
  min-width: 110px;
  padding: 10px 8px !important;
}

/* Input de tempo */
.time-input {
  width: 90px;
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
  background-color: #ffffff;
  color: #1e293b;
}

.time-input:disabled {
  background-color: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.7;
  color: #64748b;
}

.time-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

[data-theme="dark"] .time-input {
  background-color: rgba(37, 37, 41, 0.9);
  border-color: #52525b;
  color: #f4f4f5;
}

[data-theme="dark"] .time-input:disabled {
  background-color: rgba(26, 26, 29, 0.8);
  color: #a1a1aa;
}

[data-theme="dark"] .time-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.15);
}

/* Manter cor verde nos checkboxes desabilitados quando marcados */
input[type="checkbox"]:disabled:checked {
  accent-color: green !important;
  background-color: green !important;
  border-color: green !important;
  opacity: 0.8 !important;
  cursor: not-allowed;
}

/* Para melhor compatibilidade entre navegadores */
input[type="checkbox"]:disabled:checked {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: green !important;
  border: 2px solid green !important;
  border-radius: 3px;
  width: 18px;
  height: 18px;
  position: relative;
  cursor: not-allowed;
}

/* Adicionar o checkmark nos checkboxes desabilitados marcados */
input[type="checkbox"]:disabled:checked::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 13px;
  font-weight: bold;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  line-height: 1;
}

/* Checkboxes normais */
input[type="checkbox"]:not(:disabled) {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3b82f6;
}

[data-theme="dark"] input[type="checkbox"]:not(:disabled) {
  accent-color: #60a5fa;
}

/* Tema escuro - checkboxes desabilitados */
[data-theme="dark"] input[type="checkbox"]:disabled:checked {
  background-color: #10b981 !important;
  border-color: #10b981 !important;
  accent-color: #10b981 !important;
}

[data-theme="dark"] input[type="checkbox"]:disabled:checked::after {
  color: white;
}

/* Estilos para o seletor de tempo */
.time-selector-container {
  position: relative;
  display: inline-block;
  width: 100%;
}

.tooltip-container {
  cursor: help;
}

.time-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
}

.time-select {
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 6px 4px;
  font-size: 12px;
  background-color: #ffffff;
  color: #1e293b;
  width: 52px;
  text-align: center;
  cursor: pointer;
}

.time-select:disabled {
  background-color: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
  opacity: 0.7;
}

.time-separator {
  font-weight: bold;
  color: #475569;
  font-size: 13px;
}

[data-theme="dark"] .time-separator {
  color: #e4e4e7;
}

.hours-select,
.minutes-select {
  max-width: 52px;
}

/* Hover effects */
.time-select:not(:disabled):hover {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

[data-theme="dark"] .time-select {
  background-color: rgba(37, 37, 41, 0.9);
  border-color: #52525b;
  color: #f4f4f5;
}

[data-theme="dark"] .time-select:disabled {
  background-color: rgba(26, 26, 29, 0.8);
  color: #a1a1aa;
}

[data-theme="dark"] .time-select:not(:disabled):hover {
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.15);
}

/* Focus effects */
.time-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

[data-theme="dark"] .time-select:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.15);
}

/* Estilos para opções dos selects no tema dark */
[data-theme="dark"] .time-select option {
  background-color: rgba(37, 37, 41, 0.95);
  color: #f4f4f5;
}

[data-theme="dark"] .time-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
}

/* Responsividade Mobile */
@media (max-width: 768px) {
  .lotteries-accordion {
    overflow: visible;
  }
  
  .accordion-item {
    margin-bottom: 10px;
  }
  
  .accordion-header {
    padding: 12px 16px;
  }
  
  .lottery-name {
    font-size: 14px;
  }
  
  .lottery-count {
    font-size: 12px;
  }
  
  .accordion-content {
    overflow-x: auto;
    overflow-y: visible;
  }
  
  .styled-table {
    font-size: 11px;
    min-width: 750px;
  }
  
  .styled-table th,
  .styled-table td {
    padding: 8px 6px;
  }
  
  .schedule-cell {
    min-width: 130px;
    font-size: 11px;
  }
  
  .time-input {
    width: 80px;
    font-size: 12px;
    padding: 5px 6px;
  }
  
  .time-select {
    width: 48px;
    font-size: 11px;
    padding: 5px 3px;
  }
  
  .checkbox-cell {
    width: 45px;
  }
  
  input[type="checkbox"] {
    width: 16px !important;
    height: 16px !important;
  }
}
</style>
